import { TextInput, NumberInput, Switch, Select } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { ProfileField } from './api'
import classes from './Onboarding.module.css'
import { useMemo } from 'react'
import { getData as getCountries } from 'country-list'
import { Typography } from '../common/typography'

interface ProfileFieldInputProps {
  profileField: ProfileField
  value: string | number | boolean | undefined
  onChange: (key: string, value: string | number | boolean) => void
  error?: boolean
}

export default function ProfileFieldInput({
  profileField,
  value,
  onChange,
  error = false,
}: ProfileFieldInputProps) {
  if (profileField.kind === 'text') {
    return (
      <TextInput
        label={
          <div className={classes.label}>
            <Typography fs="sm" fw={500} lh="140">
              {profileField.name}
            </Typography>
            {!profileField.is_required && (
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="165"
              >
                (Optional)
              </Typography>
            )}
          </div>
        }
        placeholder={`Enter ${profileField.name.toLowerCase()}`}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(profileField.key, e.currentTarget.value)}
        classNames={{
          root: classes.root,
          input: classes.input,
          error: classes.error,
        }}
        error={
          error ? (
            <Typography fs="sm" fc={{ light: 'red-0', dark: 'red-0' }} lh="150">
              Required field.
            </Typography>
          ) : (
            ''
          )
        }
      />
    )
  }

  if (profileField.kind === 'number') {
    return (
      <NumberInput
        label={
          <div className={classes.label}>
            <Typography fs="sm" fw={500} lh="140">
              {profileField.name}
            </Typography>
            {!profileField.is_required && (
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="165"
              >
                (Optional)
              </Typography>
            )}
          </div>
        }
        placeholder={`Enter ${profileField.name.toLowerCase()}`}
        value={typeof value === 'number' ? value : undefined}
        onChange={(val) =>
          onChange(profileField.key, typeof val === 'number' ? val : 0)
        }
        classNames={{
          root: classes.root,
          input: classes.input,
          error: classes.error,
        }}
        error={
          error ? (
            <Typography fs="sm" fc={{ light: 'red-0', dark: 'red-0' }} lh="150">
              Required field.
            </Typography>
          ) : (
            ''
          )
        }
      />
    )
  }

  if (profileField.kind === 'boolean') {
    return (
      <Switch
        label={
          <div className={classes.label}>
            <Typography fs="sm" fw={500} lh="140">
              {profileField.name}
            </Typography>
            {!profileField.is_required && (
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="165"
              >
                (Optional)
              </Typography>
            )}
          </div>
        }
        labelPosition="left"
        checked={typeof value === 'boolean' ? value : false}
        onChange={(e) => onChange(profileField.key, e.currentTarget.checked)}
        classNames={{
          root: classes.switchRoot,
          label: classes.switchLabel,
          body: classes.switchBody,
          track: classes.switchTrack,
          thumb: classes.switchThumb,
        }}
      />
    )
  }

  if (profileField.kind === 'date') {
    return (
      <DatePickerInput
        label={
          <div className={classes.label}>
            <Typography fs="sm" fw={500} lh="140">
              {profileField.name}
            </Typography>
            {!profileField.is_required && (
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="165"
              >
                (Optional)
              </Typography>
            )}
          </div>
        }
        placeholder={`Select ${profileField.name.toLowerCase()}`}
        value={typeof value === 'string' && value !== '' ? value : null}
        onChange={(val) =>
          onChange(profileField.key, val && typeof val === 'string' ? val : '')
        }
        clearable
        classNames={{
          root: classes.root,
          input: classes.input,
          error: classes.error,
        }}
        clearButtonProps={{ className: classes.clearButton }}
        valueFormat="DD.MM.YYYY"
        error={
          error ? (
            <Typography fs="sm" fc={{ light: 'red-0', dark: 'red-0' }} lh="150">
              Required field.
            </Typography>
          ) : (
            ''
          )
        }
      />
    )
  }

  if (profileField.kind === 'select' && profileField.choices.length > 0) {
    return (
      <Select
        label={
          <div className={classes.label}>
            <Typography fs="sm" fw={500} lh="140">
              {profileField.name}
            </Typography>
            {!profileField.is_required && (
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="165"
              >
                (Optional)
              </Typography>
            )}
          </div>
        }
        placeholder={`Select ${profileField.name.toLowerCase()}`}
        data={profileField.choices}
        value={typeof value === 'string' && value !== '' ? value : null}
        onChange={(val) => onChange(profileField.key, val || '')}
        clearable
        classNames={{
          root: classes.root,
          input: classes.input,
          error: classes.error,
          dropdown: classes.dropdown,
        }}
        clearButtonProps={{ className: classes.clearButton }}
        comboboxProps={{ withinPortal: false }}
        error={
          error ? (
            <Typography fs="sm" fc={{ light: 'red-0', dark: 'red-0' }} lh="150">
              Required field.
            </Typography>
          ) : (
            ''
          )
        }
      />
    )
  }

  if (profileField.kind === 'country') {
    const countryNames = useMemo(
      () => getCountries().map((country) => country.name),
      []
    )

    return (
      <Select
        label={
          <div className={classes.label}>
            <Typography fs="sm" fw={500} lh="140">
              {profileField.name}
            </Typography>
            {!profileField.is_required && (
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="165"
              >
                (Optional)
              </Typography>
            )}
          </div>
        }
        placeholder={`Select ${profileField.name.toLowerCase()}`}
        data={countryNames}
        value={typeof value === 'string' && value !== '' ? value : null}
        onChange={(val) => onChange(profileField.key, val || '')}
        clearable
        searchable
        classNames={{
          root: classes.root,
          input: classes.input,
          error: classes.error,
          dropdown: classes.dropdown,
        }}
        clearButtonProps={{ className: classes.clearButton }}
        comboboxProps={{ withinPortal: false }}
        error={
          error ? (
            <Typography fs="sm" fc={{ light: 'red-0', dark: 'red-0' }} lh="150">
              Required field.
            </Typography>
          ) : (
            ''
          )
        }
      />
    )
  }

  return null
}
